/* eslint-disable no-inner-declarations */
import {
  ButtonLink,
  Cards,
  ComponentContainer,
  CustomSpacing,
  DisplayInput,
  Flex,
  Image,
  ModalContainer,
  Reusable,
  Section,
  Text,
  theme,
} from '@genially/public-web-components';
import { FC, useContext, useEffect, useRef, useState } from 'react';

import type { BackgroundDTO } from 'core/infrastructure/parseBackground';
import type { DisplayOption } from '@genially/public-web-components';
import EmptyState_NoResults from '../../views/assets/EmptyState_NoResults.png';
import MDSpinner from 'react-md-spinner';
import { SearchParamContext } from '../../context/SearchContext';
import { parseBackground } from 'core/infrastructure/parseBackground';
import { parseMedia } from 'core/infrastructure/parseMedia';
import { rem } from 'polished';
import { snowplowWrapper } from 'core/services/snowplow';
import { translate } from 'core/infrastructure/translator';

export interface ParagraphCardsGridItemDTO {
  entity: any;
}

export interface ParagraphTemplatesSearcherContentProps {
  behaviorSettings: {
    rowStyle: { background: BackgroundDTO; spacing: CustomSpacing };
  };
  buttonStyle?: {
    size: string;
    variant: 'academy' | 'danger' | 'primary' | 'secondary' | 'stroke' | 'tertiary' | 'strokeWhite';
  };
  baseURL: string;
  endpoint?: string;
  highlightedCollection?: string;
  customQuery?: string;
  pagelangcode: {
    value: string;
  };
}

interface Reusable {
  id: string;
  name: string;
  team: {
    id: string;
    name: string;
    communityProfileId: string;
  };
  userId: string;
  description: string;
  responsive: string | null;
  imageRender: string;
  imageRenderAt: string; // ISO 8601 date string
  pxDensity: number | null;
  isPremium: boolean;
  tags: string[];
  language: string;
  category: string;
  subjectArea: string;
  educationStage: string;
  thumbnail: string;
  reuseCount: number;
  score: number;
  paginationToken: string;
}

interface CommunityProfileData {
  id?: string;
  communityProfileId?: string;
  name: string;
  username?: string;
  banner?: string;
  logo?: string;
  localization?: string;
  description?: string;
  totalLikes?: number;
  totalReuses?: number;
  featuredIn?: string[];
}

interface CommunityProfileCard {
  media: any;
  communityProfile: CommunityProfileData;
  title: {
    text: string;
  };
  subtitle: {
    text: string;
  };
  url: string;
}

export const ParagraphTemplatesSearcherContent: FC<ParagraphTemplatesSearcherContentProps> = ({
  behaviorSettings,
  baseURL,
  endpoint,
  highlightedCollection,
  customQuery,
  pagelangcode,
}) => {
  const forceSentenceCase = (text?: string): string => {
    if (!text || !text.trim()) return text ?? '';
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  };

  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [error, setError] = useState(null);
  const [scrollFetching, setScrollFetching] = useState(true);
  const [geniallys, setGeniallys] = useState<any[]>([]);
  const [singleModal, setSingleModalContent] = useState<any[]>([]);
  const [slug, setSlug] = useState<string>('');
  const { searchTerm } = useContext(SearchParamContext);
  const cardsRef = useRef<HTMLDivElement>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [resultCount, setResultCount] = useState(0);
  const [opened, setOpened] = useState(false);
  const [cdpUrl, setCdpUrl] = useState<string>('');
  const [skip, setSkip] = useState(0);
  const [page, setPage] = useState(1);
  const [isOpenOutside, setIsOpenOutside] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [displayEmptyState, setDisplayEmptyState] = useState(false);
  const PAGE_SIZE = 50;
  const PAGE_SIZE_CDP = 1;
  const [sortBy, setSortBy] = useState<DisplayOption | { value: 'relevance'; label: 'Relevance' }>({
    value: 'relevance',
    label: 'Relevance',
  });
  const [sizeFilter, setSizeFilter] = useState<DisplayOption | undefined>();
  const [filtersReady, setFiltersReady] = useState(false);

  const [isCommunityContent, setIsCommunityContent] = useState(false);
  const [displayCommunityProfile, setDisplayCommunityProfiles] = useState(false);
  const [communityProfiles, setCommunityProfiles] = useState<CommunityProfileCard[]>([]);

  useEffect(() => {
    setIsCommunityContent(
      typeof baseURL === 'string' && baseURL.includes('genially-searcher.genially.com')
    );
  }, [baseURL]);

  const translateFromContent = (key: string) =>
    translate(pagelangcode ? pagelangcode.value : 'en', key);
  const likesText = translateFromContent('community_likes');
  const reusesText = translateFromContent('community_reuses');
  const useButtonText = translateFromContent('community_usetemplate');
  const emptyStateButton = translateFromContent('community_emptystatebutton');
  const emptyStateMainText = translateFromContent('community_emptystatemaintext');
  const emptyStateSecText = translateFromContent('community_emptystatesectext');
  const emptyStateCDPButton = translateFromContent('search_emptystatebutton');
  const emptyStateCDPMainText = translateFromContent('search_emptystatemaintext');
  const emptyStateCDPSecText = translateFromContent('search_emptystatesectext');
  const premiumGenially = translateFromContent('premiumgenially');
  const modalMoreOptions = translateFromContent('community_modal_moreoptions');
  const modalViewProfile = translateFromContent('community_modal_viewprofile');
  const modalMoreFromUser = translateFromContent('community_modal_morefromuser');
  const modalTags = translateFromContent('community_modal_tags');
  const modalTagsShowMore = translateFromContent('community_modal_tags_show_more');
  const modalTagsShowLess = translateFromContent('community_modal_tags_show_less');
  const allFormatsLabel = translateFromContent('community_filters_all_formats');
  const horizontalLabel = translateFromContent('community_filters_horizontal');
  const verticalLabel = translateFromContent('community_filters_all_vertical');
  const squareLabel = translateFromContent('community_filters_square');
  const mobileLabel = translateFromContent('community_filters_mobile');
  const clearButton = translateFromContent('community_filter_clearbutton');
  const relevanceLabel = translateFromContent('community_sortby_relevance');
  const newLabel = translateFromContent('community_sortby_new');
  const freeLabel = translateFromContent('community_sortby_free');
  const premiumLabel = translateFromContent('community_sortby_premium');
  const orderByText = translateFromContent('community_sortby_text');

  const parseCommunityProfiles = (profiles: CommunityProfileData[]): CommunityProfileCard[] => {
    return profiles.map((profile: CommunityProfileData) => {
      const initials = profile.name
        ? profile.name
            .split(' ')
            .map((word: string) => word[0])
            .join('')
            .toUpperCase()
            .slice(0, 2)
        : '?';

      const imageUrl = profile.banner;

      // Si hay imagen, usar parseMedia; si no, usar avatar con iniciales
      let media;
      if (imageUrl) {
        const profileMedia = {
          entity: {
            type: 'MediaImage',
            mediaImage: {
              alt: profile.name || 'User profile',
              title: profile.name || 'User profile',
              width: 200,
              height: 200,
              src: imageUrl,
            },
          },
        };
        media = parseMedia(profileMedia, true, 'en', profile.name || '');
      } else {
        // Fallback a avatar con iniciales
        media = {
          type: 'avatar',
          initials: initials,
          alt: profile.name || 'User profile',
        };
      }

      return {
        media: media,
        communityProfile: profile,
        title: {
          text: profile.name || 'Unknown',
        },
        subtitle: {
          text: profile.username ? `@${profile.username}` : '',
        },
        url: `/community/profile/?id=${profile.communityProfileId}`,
      };
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-inferrable-types
  const fetchGeniallys = async (isLoadMore: boolean = false) => {
    if (scrollFetching === false) {
      return;
    }
    try {
      let urlToFetch = '';

      const isCommunityContent =
        typeof baseURL === 'string' && baseURL.includes('genially-searcher.genially.com');

      // para CDP
      // endpoint = null
      if (!isCommunityContent && (slug || searchTerm)) {
        const querySearch = searchTerm || slug || '';
        urlToFetch = `${baseURL}?lang=${
          pagelangcode ? (pagelangcode.value === 'pt-br' ? 'pt' : pagelangcode.value) : 'en'
        }&limit=50&search=${encodeURIComponent(querySearch)}`;
      }

      // Para community
      const sortParam = sortBy?.value ? `&sortBy=${encodeURIComponent(sortBy.value)}` : '';
      const sizeParam = sizeFilter?.value ? `&size=${encodeURIComponent(sizeFilter.value)}` : '';
      // endpoint = genially-search
      if (
        isCommunityContent &&
        endpoint?.includes('genially-search') &&
        !customQuery &&
        (slug || searchTerm)
      ) {
        const querySearch = searchTerm || slug || '';
        urlToFetch = `${baseURL}geniallys?searchTerm=${encodeURIComponent(
          querySearch
        )}${sortParam}${sizeParam}`;
      }

      // sin resultados
      if (
        (endpoint?.includes('genially-search') || endpoint === null || endpoint === undefined) &&
        !customQuery &&
        !slug &&
        !searchTerm
      ) {
        setGeniallys([]);
        // setLoading(false);
        setDisplayEmptyState(true);
      }

      // Para cualquier consulta estandar con query params
      if (customQuery) {
        urlToFetch = `${baseURL}${endpoint}?${customQuery}${sortParam}${sizeParam}`;
      } else if (isCommunityContent && !endpoint?.includes('genially-search')) {
        urlToFetch = `${baseURL}${endpoint}`;
      }

      // Añadir el token de paginación si existe y estamos cargando más
      if (isLoadMore) {
        if (isCommunityContent) {
          urlToFetch += urlToFetch.includes('?') ? `&skip=${skip}` : `?skip=${skip}`;
        } else {
          urlToFetch += `&page=${page === 1 ? page + 1 : page}`;
        }
      }

      const response = await fetch(urlToFetch);
      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }

      const data = await response.json();

      if (urlToFetch.includes('profile')) {
        setDisplayCommunityProfiles(true);
        const parsedProfiles = parseCommunityProfiles(data);
        setCommunityProfiles(parsedProfiles);
        setLoading(false);
      }

      let count = data.count;

      const excludedIds = ['670e6a92ef66b9bab84d2787', '67a01d31220f1424e4feb060'];

      let fetchedGeniallys: any[] = [];

      if (isCommunityContent) {
        fetchedGeniallys =
          endpoint?.includes('highlighted') && highlightedCollection
            ? data.featured.slice(0, 16).filter((item: any) => !excludedIds.includes(item.id))
            : data.results.length > 0
            ? data.results
            : [];
      } else {
        fetchedGeniallys = data.templates;
        count = data.total > 0 ? data.total : 0;
      }

      function teaserImage(item: any) {
        return {
          entity: {
            type: 'MediaImage',
            mediaImage: {
              alt: item.name ? item.name : '',
              title: item.name ? item.name : '',
              width: 1200,
              height: 675,
              src: isCommunityContent ? item.thumbnail : item.imageUrl,
            },
          },
        };
      }

      // sendPageView function for modal opens only (not on component mount)
      let sendPageView: (() => void) | undefined;
      if (!process.env.GATSBY_IS_LOCAL || process.env.GATSBY_IS_LOCAL === 'false') {
        sendPageView = () => {
          // Snowplow tracking for modal opens
          snowplowWrapper.initTracker();
          snowplowWrapper.sendPageViewEvent();
        };
      }

      const parsedTemplates: any[] = [];
      fetchedGeniallys?.map((item: any) => {
        const normalizedName = forceSentenceCase(item.name);
        item.likesText = likesText;
        item.reusesText = reusesText;
        item.url = isCommunityContent ? '' : '/templates/' + item.id + '/';
        item.useButtonText = useButtonText;
        item.premiumGenially = premiumGenially;
        item.modalMoreOptions = modalMoreOptions;
        item.modalViewProfile = modalViewProfile;
        item.modalMoreFromUser = modalMoreFromUser;
        item.modalTags = modalTags;
        item.modalTagsShowMore = modalTagsShowMore;
        item.modalTagsShowLess = modalTagsShowLess;
        item.lang = pagelangcode.value;
        item.language = pagelangcode.value;
        const parsed = {
          snowplowEvent: sendPageView ? sendPageView : undefined,
          media: parseMedia(teaserImage(item), true, 'en', normalizedName),
          title: {
            text: normalizedName,
          },
          url: item.webUrl,
          fullTemplate: item,
          templateCard: true,
        };
        parsedTemplates.push(parsed);
      });

      if (isLoadMore && endpoint !== 'genially/highlighted') {
        const lastPrevId =
          geniallys.length > 0 ? geniallys[geniallys.length - 1].fullTemplate.id : null;
        const firstNewId = parsedTemplates.length > 0 ? parsedTemplates[0].fullTemplate.id : null;

        if (lastPrevId && firstNewId && lastPrevId === firstNewId) {
          setIsLoadingMore(false);
          return;
        }
        if (parsedTemplates.length === 0) {
          setScrollFetching(false);
        }
        setGeniallys(prevGeniallys => [...prevGeniallys, ...parsedTemplates]);
        if (parsedTemplates.length > 0) {
          if (isCommunityContent) {
            setSkip(prev => prev + PAGE_SIZE);
          } else {
            setPage(prev => prev + PAGE_SIZE_CDP + 1);
          }
        }
      } else {
        setGeniallys(parsedTemplates);
        // if (parsedTemplates.length === 0) {
        //   setLoading(false);
        //   setDisplayEmptyState(true);
        // }
        setLoading(false);
        setResultCount(count ? count : 0);
        setSkip(PAGE_SIZE);
        setPage(PAGE_SIZE_CDP);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const formats = [
    { value: '', label: allFormatsLabel },
    { value: 'horizontal', label: horizontalLabel },
    { value: 'infographic', label: verticalLabel },
    { value: 'mobile', label: mobileLabel },
    { value: 'square', label: squareLabel },
  ];

  const sorts = [
    { value: 'relevance', label: relevanceLabel },
    { value: 'updatedAt', label: newLabel },
    { value: 'freeFirst', label: freeLabel },
    { value: 'premiumFirst', label: premiumLabel },
  ];

  useEffect(() => {
    if (!filtersReady) return;
    updateUrlParams(sortBy, sizeFilter);
    fetchGeniallys();
  }, [filtersReady, slug, searchTerm, sortBy, sizeFilter]);

  function updateUrlParams(sortBy: DisplayOption, sizeFilter: DisplayOption | undefined) {
    const params = new URLSearchParams(window.location.search);
    if (sortBy && sortBy.value !== 'relevance') params.set('sortBy', sortBy.value);
    else params.delete('sortBy');
    if (sizeFilter && sizeFilter.value) params.set('size', sizeFilter.value);
    else params.delete('size');
    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
  }

  useEffect(() => {
    if (endpoint?.includes('highlighted')) return;
    const handleScroll = () => {
      if (!cardsRef.current || isLoadingMore) {
        return;
      }

      if (geniallys.length >= resultCount) {
        setScrollFetching(false);
        return;
      }

      const cardsElement = cardsRef.current;
      const rect = cardsElement.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      if (rect.bottom <= windowHeight) {
        setIsLoadingMore(true);
        fetchGeniallys(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isLoadingMore, geniallys.length, resultCount]);

  useEffect(() => {
    const slug = new URLSearchParams(window.location.search);
    const searchTermFromUrl = slug.get('searchTerm') ?? '';
    setSlug(decodeURIComponent(searchTermFromUrl));
  }, []);

  function getParamFromUrl(paramName: string): string | null {
    if (typeof window === 'undefined') return null;

    const params = new URLSearchParams(window.location.search);
    let value = params.get(paramName);

    if (!value && window.location.hash) {
      const hash = window.location.hash.substring(1);
      const queryIndex = hash.indexOf('?');
      if (queryIndex !== -1) {
        const hashParams = new URLSearchParams(hash.substring(queryIndex));
        value = hashParams.get(paramName);
      } else {
        const hashParams = new URLSearchParams(hash);
        value = hashParams.get(paramName);
      }
    }
    return value;
  }

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if ((window as any).__modalViewAlreadyOpened) return;
    const modalViewParam = getParamFromUrl('modalView');
    if (modalViewParam) {
      (window as any).__modalViewAlreadyOpened = true;
      fetchModalContent(modalViewParam);
      setOpened(true);
    }
  }, []);

  const fetchModalContent = async (id: string) => {
    try {
      const urlToFetch = `https://genially-searcher.genially.com/api/genially/${id}`;
      const response = await fetch(urlToFetch);
      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }

      const data = await response.json();
      data.likesText = likesText;
      data.reusesText = reusesText;
      data.useButtonText = useButtonText;
      data.premiumGenially = premiumGenially;
      data.modalMoreOptions = modalMoreOptions;
      data.modalViewProfile = modalViewProfile;
      data.modalMoreFromUser = modalMoreFromUser;
      data.modalTags = modalTags;
      data.modalTagsShowMore = modalTagsShowMore;
      data.modalTagsShowLess = modalTagsShowLess;
      data.language = pagelangcode.value;
      setSingleModalContent([data]);
    } catch (err: any) {
      setError(err.message);
    }
  };

  useEffect(() => {
    const handleHashChange = () => {
      if (!window.location.hash) {
        return;
      }

      if (window.location.hash !== '#modalView') {
        return;
      }

      const handleModalClose = () => {
        window.history.pushState(
          '',
          document.title,
          window.location.pathname + window.location.search
        );
      };

      document.addEventListener('click', handleModalClose, { once: true });
    };

    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sortParam = params.get('sortBy') || 'relevance';
    const sizeParam = params.get('size') || '';

    const sortOpt = sorts.find(o => o.value === sortParam) || sorts[0];
    const sizeOpt = formats.find(o => o.value === sizeParam) || undefined;

    setSortBy(sortOpt);
    setSizeFilter(sizeOpt);
    setFiltersReady(true);
  }, []);

  useEffect(() => {
    function getCDPurl(lang: string) {
      let url = !isCommunityContent ? '/community/templates/' : '/templates/';
      switch (lang) {
        case 'pt-br': {
          url = !isCommunityContent
            ? '/' + lang + '/comunidade/modelos/'
            : '/' + lang + '/modelos/';
          break;
        }
        case 'es': {
          url = !isCommunityContent
            ? '/' + lang + '/comunidad/plantillas/'
            : '/' + lang + '/templates/';
          break;
        }
        case 'de': {
          url = !isCommunityContent
            ? '/' + lang + '/community/vorlagen/'
            : '/' + lang + '/vorlagen/';
          break;
        }
        case 'fr': {
          url = !isCommunityContent
            ? '/' + lang + '/communaute/modeles/'
            : '/' + lang + '/modeles/';
          break;
        }
        case 'it': {
          url = !isCommunityContent
            ? '/' + lang + '/community/template/'
            : '/' + lang + '/templates/';
          break;
        }
        default: {
          url = !isCommunityContent ? '/community/templates/' : '/templates/';
          break;
        }
      }
      return url;
    }
    setCdpUrl(
      getCDPurl(pagelangcode.value) +
        (isCommunityContent ? '?from=community-empty-state' : '?from=templates-empty-state')
    );
  }, [isCommunityContent]);

  if (!baseURL) {
    return <p>BaseURL is missing</p>;
  }

  if (
    (endpoint?.includes('genially-search') ||
      (!isCommunityContent && (endpoint === null || endpoint === undefined))) &&
    !searchTerm &&
    !slug
  )
    return null;

  if (displayCommunityProfile && communityProfiles.length > 0) {
    return (
      <Section background={parseBackground(behaviorSettings?.rowStyle?.background)} flex>
        <ComponentContainer customSpacing={behaviorSettings?.rowStyle?.spacing}>
          <Cards
            variant="carousel2"
            firstSlider={communityProfiles.slice(0, 15)}
            communityProfile
          />
        </ComponentContainer>
      </Section>
    );
  }

  return (
    <Section background={parseBackground(behaviorSettings?.rowStyle?.background)} flex>
      <ComponentContainer customSpacing={behaviorSettings?.rowStyle?.spacing}>
        {isCommunityContent && !endpoint?.includes('highlighted') && (
          <Flex
            css={`
              justify-content: center;
              gap: ${rem(16)};
              @media ${theme.device.tablet} {
                justify-content: space-between;
                padding-bottom: ${rem(16)};
              }
            `}
          >
            <DisplayInput
              value={sizeFilter?.value ? sizeFilter.value : undefined}
              onSelectOption={setSizeFilter}
              preset="standard"
              options={formats}
              hasParentIcon
              size="medium"
              dropDownButtonClear={clearButton}
            />
            <Flex
              alignItems="center"
              justifyContent={'end'}
              onClick={() => setIsOpenOutside(prevIsOpen => !prevIsOpen)}
              css={`
                cursor: pointer;
              `}
            >
              <Flex
                css={`
                  display: none;
                  @media ${theme.device.tablet} {
                    display: unset;
                  }
                `}
              >
                <Text variant="paragraphS" style={{ minWidth: rem(56), whiteSpace: 'nowrap' }}>
                  {orderByText}
                </Text>
              </Flex>
              <DisplayInput
                value={sortBy?.value ? sortBy.value : undefined}
                onSelectOption={setSortBy}
                preset="minimal"
                options={sorts}
                hasParentIcon
                size="small"
                isOpenOutside={isOpenOutside}
              />
            </Flex>
          </Flex>
        )}
        <Flex flexDirection={'column'} alignItems="center" justifyContent="center">
          <p style={{ textAlign: 'center' }}>
            {loading && (
              <>
                <MDSpinner size={32} singleColor={theme.colors.rBlack} />
              </>
            )}
          </p>
          {singleModal.length > 0 && opened && (
            <ModalContainer
              opened={opened}
              setOpened={setOpened}
              fromCommunity
              borderRadius={16}
              scrollbar={false}
            >
              <>
                <></>
                <Reusable content={singleModal[0]} />
              </>
            </ModalContainer>
          )}
          {!loading && geniallys.length > 0 && (
            <Flex>
              <div ref={cardsRef}>
                <Cards
                  customSpacing={behaviorSettings?.rowStyle?.spacing}
                  background={parseBackground(behaviorSettings?.rowStyle?.background)}
                  cards={geniallys}
                  variant="grid"
                  masonry={endpoint?.includes('highlighted') ? false : true}
                  templateCard
                  elementOpenOnModal={isCommunityContent ? true : false}
                  socialProfile={isCommunityContent && geniallys[0].team ? true : false}
                  isLoading={!scrollFetching ? false : isLoadingMore}
                />
              </div>
            </Flex>
          )}
          {!loading && geniallys.length === 0 && (
            <Flex
              flexDirection={'column'}
              marginBottom="medium"
              alignItems="center"
              justifyContent="center"
              style={{ paddingTop: rem(48), paddingBottom: rem(104), maxWidth: rem(448) }}
            >
              <Image
                src={EmptyState_NoResults}
                alt={'EmptyStateImg'}
                width={'120.5'}
                height={'104'}
                style={{ maxHeight: rem(128), maxWidth: rem(128) }}
              />
              <Text variant="paragraphMBold" style={{ paddingTop: rem(16) }}>
                {isCommunityContent ? emptyStateMainText : emptyStateCDPMainText}
              </Text>
              <Text variant="paragraphS" style={{ textAlign: 'center', paddingTop: rem(8) }}>
                {isCommunityContent ? emptyStateSecText : emptyStateCDPSecText}
              </Text>
              <ButtonLink
                variant="primary"
                href={cdpUrl}
                size="default"
                rel="noreferrer"
                style={{ paddingTop: rem(16) }}
              >
                {isCommunityContent ? emptyStateButton : emptyStateCDPButton}
              </ButtonLink>
            </Flex>
          )}
        </Flex>
      </ComponentContainer>
    </Section>
  );
};
